'use client';
import { SearchFilterStructureRo } from '@/types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { getUnitList } from '@/common/helpers';
import { applyCountersToMenu, filterValidCodes, type MenuState } from './applyCountersToMenu';
import { OfferType, OfferTypeObject } from '@/types';

const EMPTY_RANGES: MenuState['rangesDimensions'] = {
  roomNumber: [],
  price: [],
  rentPrice: [],
  area: [],
};

const EMPTY_MENU: MenuState = {
  offerTypeList: [],
  categoriesMenuList: [],
  equipmentMenuList: [],
  unitList: undefined,
  rangesDimensions: EMPTY_RANGES,
  powerConsumptionClassesList: [],
  energyClassFactorsList: [],
};

const buildOfferTypeList = (t: ReturnType<typeof useTranslations>): OfferTypeObject[] => [
  { code: OfferType.SELL, title: t('sell'), count: 0 },
  { code: OfferType.RENT, title: t('rent'), count: 0 },
];

const buildMenuFromData = (
  data: SearchFilterStructureRo,
  t: ReturnType<typeof useTranslations>,
): MenuState => {
  const categoriesMenuList = data.categories ?? [];
  const equipmentMenuList = data.equipment ?? [];

  let unitList: MenuState['unitList'];
  if (data.countries && data.federalStates && data.districts) {
    unitList = getUnitList(data.countries, data.federalStates, data.districts);
  }

  return {
    offerTypeList: buildOfferTypeList(t),
    categoriesMenuList,
    equipmentMenuList,
    unitList,
    rangesDimensions: {
      roomNumber: [data.roomsNumberMinimum, data.roomsNumberMaximum],
      price: [data.originalSellPriceMinimum, data.originalSellPriceMaximum],
      rentPrice: [data.rentPriceMinimum, data.rentPriceMaximum],
      area: [data.areaFromMinimum, data.areaFromMaximum],
    },
    powerConsumptionClassesList: filterValidCodes(data.powerConsumptionClasses ?? []),
    energyClassFactorsList: filterValidCodes(data.energyClassFactors ?? []),
  };
};

export const useHousingInitialFilters = (initialData: SearchFilterStructureRo | null) => {
  const t = useTranslations();

  const [menu, setMenu] = useState<MenuState>(() =>
    initialData ? buildMenuFromData(initialData, t) : EMPTY_MENU,
  );

  useEffect(() => {
    if (!initialData) return;
    setMenu(buildMenuFromData(initialData, t));
  }, [initialData, t]);

  const setCountersData = useCallback(
    (filterData: Parameters<typeof applyCountersToMenu>[1]) =>
      setMenu((prev) => applyCountersToMenu(prev, filterData)),
    [],
  );

  return {
    offerTypeList: menu.offerTypeList,
    categoriesMenuList: menu.categoriesMenuList,
    equipmentMenuList: menu.equipmentMenuList,
    unitList: menu.unitList,
    rangesDimensions: menu.rangesDimensions,
    powerConsumptionClassesList: menu.powerConsumptionClassesList,
    energyClassFactorsList: menu.energyClassFactorsList,
    isFiltersLoading: false,
    isFiltersReady: !!initialData,
    setCountersData,
  };
};
